import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  forwardRef,
  input,
  Input,
  InputSignal,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators } from "@angular/forms";
import { InputDirective } from "@bitwarden/web-vault/app/directives/input.directive";
import { distinctUntilChanged, takeUntil } from "rxjs";

@Component({
  selector: "app-input",
  templateUrl: "./input.component.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent
  extends InputDirective
  implements OnInit, OnChanges, ControlValueAccessor
{
  @Input() label: string;
  @Input() boldLabel: boolean = true;
  @Input() isRequired: boolean = false;
  /**
   * Externally-driven error state (border only, no helper text). Use when validation
   * lives on a parent form rather than this input's internal control — lets a host show
   * the red border on submit without the "required" text shifting an inline row's height.
   */
  @Input() hasError: boolean = false;
  @Input() isDisabled: boolean = false;
  /** Minimum allowed numeric value; shows an inline "must be positive" error when below it. */
  @Input() min?: number;
  @Input() type: string = "text";
  @Input() multiline: boolean = false; // render a <textarea> instead of <input>
  @Input() placeholder: string = "";
  /** Compact variant: ~24px tall, neumorphic inset field, small text. Used for inline rows. */
  @Input() compact: boolean = false;
  manualTouched: InputSignal<boolean> = input(false);
  @Input() value: string | number = "";
  @Input() prefix: string = "";
  @Input() suffix: string = "";
  @Input() customShadowClass: string = "tw-shadow-elevation-input";
  @Input() testId?: string;
  @Output() outputValue = new EventEmitter<any>();

  // Resolves to the <input> or <textarea> rendered in the @if branch.
  @ViewChild("inputElement") private inputEl?: ElementRef<HTMLInputElement | HTMLTextAreaElement>;

  formControl: FormControl;
  onChange = (_: any) => {};
  onTouched = () => {};

  private static _idCounter = 0;
  private readonly instanceId = InputComponent._idCounter++;

  get prefixId(): string | null {
    return this.prefix ? `prefix-${this.instanceId}` : null;
  }

  handleWrapperClick(): void {
    if (this.inputEl) {
      this.onWrapperClick(this.inputEl.nativeElement);
    }
  }

  handleWrapperMouseDown(event: MouseEvent): void {
    if (this.inputEl) {
      this.onWrapperMouseDown(event, this.inputEl.nativeElement);
    }
  }

  constructor() {
    super();
    effect(() => {
      if (this.manualTouched()) {
        this.formControl.markAsTouched();
      }
    });
  }

  ngOnInit(): void {
    this.placeholder = this.setDefaultNumberPlaceholder(this.type, this.placeholder);
    const validators = this.isRequired ? [Validators.required, Validators.pattern(/\S/)] : [];
    if (this.min != null) {
      validators.push(Validators.min(this.min));
    }
    this.formControl = new FormControl(
      { value: this.value, disabled: this.isDisabled },
      validators.length ? validators : null,
    );

    // this is for the app-input in the calendar component // todo fix this
    this.formControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((newValue) => {
        this.outputValue.emit(newValue);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.placeholder = this.setDefaultNumberPlaceholder(this.type, this.placeholder);
    if (!this.formControl) {
      return;
    }

    if (changes["value"] && changes["value"].currentValue !== undefined) {
      // Update the FormControl with the new value
      this.formControl.setValue(this.value);
    }

    if (changes["isDisabled"]) {
      this.setDisabledState(this.isDisabled);
    }
  }

  clearInput(): void {
    this.formControl.markAsUntouched();
    this.formControl.setValue("");
    this.value = null;
  }

  isInvalid(): boolean {
    return (
      this.formControl.invalid &&
      (this.formControl.touched || this.formControl.dirty) &&
      !this.isFocused
    );
  }

  writeValue(value: any): void {
    this.formControl.setValue(value);
  }

  registerOnChange(func: any): void {
    this.onChange = func;
    this.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(func);
  }

  registerOnTouched(func: any): void {
    this.onTouched = func;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.isDisabled || isDisabled) {
      this.formControl.disable();
      return;
    }
    this.formControl.enable();
  }
}
